import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TaskCard from '@/components/tasks/TaskCard';
import TaskFilters from '@/components/tasks/TaskFilters';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';

interface Task {
  id: number;
  title: string;
  description: string;
  status: string;
  priority: string;
  project_id: number;
  project_name?: string;
  assignee_id?: number;
  assignee_name?: string;
  assignee_avatar?: string;
  due_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
}

interface Filters {
  search: string;
  project: string;
  assignee: string;
  priority: string;
}

const columns = [
  { id: 'todo', title: 'To Do', color: 'bg-slate-500' },
  { id: 'in_progress', title: 'In Progress', color: 'bg-blue-500' },
  { id: 'in_review', title: 'In Review', color: 'bg-yellow-500' },
  { id: 'done', title: 'Done', color: 'bg-green-500' },
  { id: 'archived', title: 'Archived', color: 'bg-gray-500' },
];

const TasksKanban = () => {
  const [filters, setFilters] = useState<Filters>({
    search: '',
    project: 'all',
    assignee: 'all',
    priority: 'all',
  });

  const queryClient = useQueryClient();

  const { data: tasks = [], isLoading } = useQuery<Task[]>({
    queryKey: ['tasks', filters],
    queryFn: async () => {
      // Mock data - replace with actual API call
      return [
        {
          id: 1,
          title: 'Setup authentication system',
          description: 'Implement JWT-based authentication with refresh tokens',
          status: 'in_progress',
          priority: 'high',
          project_id: 1,
          project_name: 'LWM PM System',
          assignee_id: 1,
          assignee_name: 'Roman Siromskyi',
          assignee_avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Roman',
          due_date: '2025-10-15',
          estimated_hours: 16,
          actual_hours: 8,
        },
        {
          id: 2,
          title: 'Design dashboard mockups',
          description: 'Create high-fidelity mockups for the main dashboard',
          status: 'todo',
          priority: 'normal',
          project_id: 1,
          project_name: 'LWM PM System',
          assignee_id: 5,
          assignee_name: 'John Designer',
          assignee_avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=John',
          due_date: '2025-10-10',
          estimated_hours: 8,
        },
        {
          id: 3,
          title: 'Fix payment gateway integration',
          description: 'Debug and fix issues with Stripe payment processing',
          status: 'in_review',
          priority: 'urgent',
          project_id: 2,
          project_name: 'Barcelona E-commerce',
          assignee_id: 2,
          assignee_name: 'Bohdan Buryi',
          assignee_avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Bohdan',
          due_date: '2025-10-05',
          estimated_hours: 12,
          actual_hours: 14,
        },
        {
          id: 4,
          title: 'Write API documentation',
          description: 'Document all API endpoints with examples',
          status: 'done',
          priority: 'low',
          project_id: 1,
          project_name: 'LWM PM System',
          assignee_id: 3,
          assignee_name: 'Vlad Dev',
          assignee_avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Vlad',
          due_date: '2025-10-01',
          estimated_hours: 6,
          actual_hours: 7,
        },
        {
          id: 5,
          title: 'Implement search functionality',
          description: 'Add full-text search across all entities',
          status: 'todo',
          priority: 'normal',
          project_id: 1,
          project_name: 'LWM PM System',
          assignee_id: 2,
          assignee_name: 'Bohdan Buryi',
          assignee_avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Bohdan',
          due_date: '2025-10-20',
          estimated_hours: 10,
        },
      ];
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ taskId, newStatus }: { taskId: number; newStatus: string }) => {
      // Mock API call - replace with actual implementation
      console.log('Updating task status:', taskId, newStatus);
      await new Promise((resolve) => setTimeout(resolve, 500));
      return { taskId, newStatus };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      toast.success('Task status updated');
    },
    onError: (error) => {
      toast.error('Failed to update task status');
      console.error(error);
    },
  });

  const handleDragStart = (e: React.DragEvent, taskId: number) => {
    e.dataTransfer.setData('taskId', taskId.toString());
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    const taskId = Number(e.dataTransfer.getData('taskId'));
    updateStatusMutation.mutate({ taskId, newStatus });
  };

  const filteredTasks = tasks.filter((task) => {
    if (filters.search && !task.title.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    if (filters.project !== 'all' && task.project_id !== Number(filters.project)) {
      return false;
    }
    if (filters.assignee !== 'all' && task.assignee_id !== Number(filters.assignee)) {
      return false;
    }
    if (filters.priority !== 'all' && task.priority !== filters.priority) {
      return false;
    }
    return true;
  });

  const getTasksByStatus = (status: string) => {
    return filteredTasks.filter((task) => task.status === status);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Task Management</h1>
          <p className="text-muted-foreground mt-1">Drag and drop tasks to update their status</p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>

      {/* Filters */}
      <TaskFilters filters={filters} onFiltersChange={setFilters} />

      {/* Kanban Board */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {columns.map((col) => (
            <div key={col.id} className="space-y-3">
              <div className="h-8 bg-muted animate-pulse rounded" />
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-32 bg-muted animate-pulse rounded" />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {columns.map((column) => {
            const columnTasks = getTasksByStatus(column.id);
            return (
              <div
                key={column.id}
                className="flex flex-col"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, column.id)}
              >
                {/* Column Header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-3 h-3 rounded-full ${column.color}`} />
                  <h3 className="font-semibold text-sm">
                    {column.title}
                    <span className="ml-2 text-muted-foreground">({columnTasks.length})</span>
                  </h3>
                </div>

                {/* Column Content */}
                <Card className="flex-1 p-3 min-h-[500px] bg-muted/30">
                  <div className="space-y-3">
                    {columnTasks.length === 0 ? (
                      <div className="text-center text-muted-foreground text-sm py-8">
                        No tasks
                      </div>
                    ) : (
                      columnTasks.map((task) => (
                        <div
                          key={task.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, task.id)}
                          className="cursor-move"
                        >
                          <TaskCard task={task} />
                        </div>
                      ))
                    )}
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TasksKanban;
